import React, { useState } from 'react';
import {
    UserCircle,
    Building2,
    Phone,
    Mail,
    Hash,
    Calendar,
    FileType,
    Tag,
    FileText,
    MapPin,
    Clock,
    HelpCircle,
    ChevronDown,
    ChevronUp,
    RefreshCw,
    Building,
    User
} from 'lucide-react';

const styles = {
    card: {
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        margin: '1rem 0',
    },
    header: {
        color: '#0A6EBD',
        fontSize: '1.5rem',
        fontWeight: '600',
        marginBottom: '1.5rem'
    },
    subHeader: {
        color: '#0A6EBD',
        fontSize: '1.2rem',
        fontWeight: '600',
        marginTop: '1.5rem'
    },
    detailsBox: {
        backgroundColor: '#f8f9fa',
        borderRadius: '6px',
        padding: '1.5rem',
        marginBottom: '2rem'
    },
    contactBox: {
        backgroundColor: '#f8f9fa',
        borderRadius: '6px',
        padding: '1.5rem',
        marginTop: '1rem'
    },
    relanceBox: {
        backgroundColor: '#fff8e8',
        borderRadius: '6px',
        padding: '1.5rem',
        marginTop: '1rem',
        border: '1px solid #ffeeba'
    },
    iconWrapper: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.75rem',
        marginBottom: '0.75rem',
        padding: '0.5rem',
        transition: 'background-color 0.2s ease',
        borderRadius: '4px',
    },
    icon: {
        color: '#F76707',
        marginTop: '3px'
    },
    relanceIcon: {
        color: '#f0ad4e',
        marginTop: '3px'
    },
    label: {
        fontWeight: '500',
        color: '#4A5568',
        minWidth: '100px'
    },
    value: {
        color: '#2D3748',
        flex: 1
    },
    longText: {
        position: 'relative',
        overflow: 'hidden'
    },
    expandButton: {
        background: 'none',
        border: 'none',
        color: '#0A6EBD',
        cursor: 'pointer',
        padding: '0.25rem 0',
        display: 'flex',
        alignItems: 'center',
        gap: '0.25rem',
        fontSize: '0.875rem'
    },
    badge: {
        display: 'inline-block',
        padding: '0.35em 0.65em',
        fontSize: '0.75em',
        fontWeight: '700',
        lineHeight: '1',
        color: '#fff',
        textAlign: 'center',
        whiteSpace: 'nowrap',
        verticalAlign: 'baseline',
        borderRadius: '0.25rem',
        backgroundColor: '#f0ad4e',
        marginLeft: '0.5rem'
    }
};

const LongTextValue = ({ text, maxLength = 150 }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    if (!text) return <span style={styles.value}>-</span>;

    const needsExpansion = text.length > maxLength;

    const displayText = isExpanded ? text : (needsExpansion ? `${text.slice(0, maxLength)}...` : text);

    return (
        <div style={styles.longText}>
            <div style={styles.value}>{displayText}</div>
            {needsExpansion && (
                <button
                    style={styles.expandButton}
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    {isExpanded ? (
                        <>Voir moins <ChevronUp size={16} /></>
                    ) : (
                        <>Voir plus <ChevronDown size={16} /></>
                    )}
                </button>
            )}
        </div>
    );
};

const DetailItem = ({ icon, label, value, isLongText = false, iconStyle = styles.icon }) => (
    <div style={styles.iconWrapper}>
        {React.cloneElement(icon, { size: 18, style: iconStyle })}
        <strong style={styles.label}>{label}:</strong>
        {isLongText ? (
            <LongTextValue text={value} />
        ) : (
            <span style={styles.value}>{value || "-"}</span>
        )}
    </div>
);

const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    } catch {
        return dateString;
    }
};

const TrackingDetails = ({ details, contactInfo }) => {
    // Accéder aux informations de relance dans le sous-objet details.details
    const relanceDetails = details.details || {};
    const isRelance = relanceDetails.isRelance === true;

    console.log("Relance details:", { isRelance, relanceDetails });

    return (
        <div className="card" style={styles.card}>
            <div className="card-body p-4">
                <h3 style={styles.header}>
                    Détails de la réclamation
                    {isRelance && <span style={styles.badge}>Relance</span>}
                </h3>

                <div style={styles.detailsBox}>
                    <div className="row">
                        <div className="col-md-6">
                            <DetailItem
                                icon={<Hash />}
                                label="Référence"
                                value={details.id}
                            />
                            <DetailItem
                                icon={<Calendar />}
                                label="Date"
                                value={details.creation}
                            />
                            <DetailItem
                                icon={<FileType />}
                                label="Type"
                                value={details.element}
                            />
                            <DetailItem
                                icon={<Tag />}
                                label="Catégorie"
                                value={details.categorie}
                            />
                            <DetailItem
                                icon={<MapPin />}
                                label="Point de service"
                                value={details.pointService}
                            />
                            <DetailItem
                                icon={<Clock />}
                                label="Durée estimée"
                                value={details.duree}
                            />
                        </div>
                        <div className="col-md-6">
                            <DetailItem
                                icon={<FileText />}
                                label="Description"
                                value={details.description}
                                isLongText
                            />
                            <DetailItem
                                icon={<HelpCircle />}
                                label="Attente"
                                value={details.attente || "Non renseigné"}
                                isLongText
                            />
                        </div>
                    </div>
                </div>

                {/* Section pour les informations de relance */}
                {isRelance && relanceDetails.dateReclamationInitiale && (
                    <>
                        <h4 style={styles.subHeader} className="d-flex align-items-center gap-2">
                            <RefreshCw size={24} />
                            Informations sur la réclamation initiale
                        </h4>

                        <div style={styles.relanceBox}>
                            <div className="row">
                                <div className="col-md-6">
                                    <DetailItem
                                        icon={<Calendar />}
                                        label="Date initiale"
                                        value={formatDate(relanceDetails.dateReclamationInitiale)}
                                        iconStyle={styles.relanceIcon}
                                    />
                                    <DetailItem
                                        icon={<FileType />}
                                        label="Introduction"
                                        value={relanceDetails.modeIntroductionInitiale}
                                        iconStyle={styles.relanceIcon}
                                    />
                                </div>
                                <div className="col-md-6">
                                    {relanceDetails.modeIntroductionInitiale === "En Agence" && relanceDetails.precisionAgence && (
                                        <DetailItem
                                            icon={<Building />}
                                            label="Agence"
                                            value={relanceDetails.precisionAgence}
                                            iconStyle={styles.relanceIcon}
                                        />
                                    )}
                                    {relanceDetails.modeIntroductionInitiale === "J'ai juste informé mon gestionnaire" && relanceDetails.nomGestionnaire && (
                                        <DetailItem
                                            icon={<User />}
                                            label="Gestionnaire"
                                            value={relanceDetails.nomGestionnaire}
                                            iconStyle={styles.relanceIcon}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                )}

                <h4 style={styles.subHeader} className="d-flex align-items-center gap-2">
                    <UserCircle size={24} />
                    Votre interlocuteur
                </h4>

                <div style={styles.contactBox}>
                    <div className="row">
                        <div className="col-md-6">
                            <DetailItem
                                icon={<UserCircle />}
                                label="Responsable"
                                value={contactInfo.responsable}
                            />
                            <DetailItem
                                icon={<Building2 />}
                                label="Service"
                                value={contactInfo.serviceResponsable}
                            />
                        </div>
                        <div className="col-md-6">
                            <DetailItem
                                icon={<Mail />}
                                label="Email"
                                value={contactInfo.contactResponsable?.email}
                            />
                            <DetailItem
                                icon={<Phone />}
                                label="Téléphone"
                                value={contactInfo.contactResponsable?.telephone}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TrackingDetails;